"use client";

import {
  format,
  startOfDay,
  startOfToday,
  subDays,
  subWeeks,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  isSameDay,
  isToday,
  isYesterday,
  isSameMonth,
  isSameYear,
  parse,
  isAfter,
  isBefore,
} from "date-fns";
import { CalendarIcon } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import { DateRange } from "react-day-picker";

import { DateInputField } from "@/dashboard/components/date/DateInputField";
import { Button } from "@/dashboard/components/ui/button";
import { Calendar } from "@/dashboard/components/ui/calendar";
import { Label } from "@/dashboard/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/dashboard/components/ui/popover";

export const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);

const MIN_DATE = new Date(1900, 0, 1);
const MAX_DATE = new Date(2100, 11, 31);

const makeRange = (from: Date, to: Date): DateRange => ({
  from: startOfDay(from),
  to: startOfDay(to),
});

type Range = { start?: Date; end?: Date };

interface Props {
  onChange: (r: Range) => void;
  start?: Date;
  end?: Date;
  label?: string;
  renderTrigger?: React.ReactNode | ((info: { formatted: string | null }) => React.ReactNode);
  contentAlign?: "start" | "center" | "end";
  /**
   * Earliest selectable day. Days before it are disabled, the calendar will not page back past it,
   * and presets that would reach behind it are hidden.
   */
  minDate?: Date;
}

export const DateRangePicker: React.FC<Props> = ({
  onChange,
  start,
  end,
  label,
  renderTrigger,
  contentAlign = "start",
  minDate,
}) => {
  const [open, setOpen] = useState(false);
  const [startInputValue, setStartInputValue] = useState("");
  const [endInputValue, setEndInputValue] = useState("");
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());

  /** keep a *local* working copy of the range until the user clicks "Done" */
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(undefined);

  /* sync local copy whenever the external value changes */
  useEffect(() => {
    if (!start) {
      setPendingRange(undefined);
      setStartInputValue("");
      setEndInputValue("");
    } else {
      // Keep 'to' undefined if not provided to avoid constraining edits
      setPendingRange({ from: start, to: end });
      setStartInputValue(format(start, "MMM d, yyyy"));
      setEndInputValue(end ? format(end, "MMM d, yyyy") : "");
    }
  }, [start, end]);

  // Set initial calendar month based on selection when opening
  useEffect(() => {
    if (open) {
      if (pendingRange?.from) {
        setCurrentMonth(pendingRange.from);
      } else {
        setCurrentMonth(new Date());
      }
    }
  }, [open, pendingRange]);

  /** ── single-date helper ─────────────────────────────── */
  function labelFor(date: Date): string {
    if (isToday(date)) {
      return "Today";
    }
    if (isYesterday(date)) {
      return "Yesterday";
    }

    return isSameYear(date, new Date()) ? format(date, "MMM d") : format(date, "MMM d, yyyy");
  }

  /** ── range formatter  ─────────── */
  const formattedDateRange = useMemo(() => {
    // nothing picked at all
    if (!start && !end) {
      return null;
    }

    /* ── one-sided ranges ───────────────────────────── */

    // user picked a *start* but left the end blank → "…–Today"
    if (start && !end) {
      return `${labelFor(start)} – Today`;
    }

    // user picked an *end* but no start → "Until <end date>"
    if (!start && end) {
      return `Before ${labelFor(end)}`;
    }

    /* ── both dates present ─────────────────────────── */
    // no end, or same day → single-date label
    if (!end || isSameDay(start!, end)) {
      return labelFor(start!);
    }

    // same month & year: "Apr 9–12"  (add year when needed)
    if (isSameYear(start!, end) && isSameMonth(start!, end)) {
      const range = `${format(start!, "MMM d")}–${format(end, "d")}`;
      return isSameYear(start!, new Date()) ? range : `${range}, ${format(end, "yyyy")}`;
    }

    // same year, different months: "Apr 9 – Jun 2"
    if (isSameYear(start!, end)) {
      return `${labelFor(start!)} – ${labelFor(end)}`;
    }

    // cross-year: "Dec 31, 2024 – Jan 3, 2025"
    return `${labelFor(start!)} – ${labelFor(end)}`;
  }, [start, end]);

  /* preset helpers */
  const today = startOfToday();
  const presets = useMemo(
    () =>
      [
        { label: "Today", range: () => makeRange(today, today) },
        { label: "Yesterday", range: () => makeRange(subDays(today, 1), subDays(today, 1)) },
        { label: "This week", range: () => makeRange(startOfWeek(today), endOfWeek(today)) },
        {
          label: "Last week",
          range: () => {
            const lw = subWeeks(today, 1);
            return makeRange(startOfWeek(lw), endOfWeek(lw));
          },
        },
        { label: "This month", range: () => makeRange(startOfMonth(today), today) },
        {
          label: "Last month",
          range: () => {
            const lm = subMonths(today, 1);
            return makeRange(startOfMonth(lm), endOfMonth(lm));
          },
        },
        { label: "Last 7 days", range: () => makeRange(subDays(today, 6), today) },
        { label: "Last 28 days", range: () => makeRange(subDays(today, 27), today) },
        { label: "Last 90 days", range: () => makeRange(subDays(today, 89), today) },
        { label: "Last 12 months", range: () => makeRange(subMonths(today, 12), today) },
      ].filter(preset => {
        if (!minDate) {
          return true;
        }
        const from = preset.range().from;
        return !from || !isBefore(from, minDate);
      }),
    [today, minDate]
  );

  const selectedPreset = useMemo(() => {
    const candidateFrom = pendingRange?.from ?? start;
    const candidateTo = pendingRange?.to ?? end;
    if (!candidateFrom || !candidateTo) {
      return "Custom";
    }
    return (
      presets.find(
        p => isSameDay(p.range().from!, candidateFrom) && isSameDay(p.range().to!, candidateTo)
      )?.label ?? "Custom"
    );
  }, [start, end, presets, pendingRange]);

  const parseInputDate = (value: string, fallback?: Date): Date | undefined => {
    if (!value.trim()) {
      return undefined;
    }
    try {
      const currentYear = new Date().getFullYear();

      // Try various date formats with years first
      const formatsWithYear = ["MMM d, yyyy", "MM/dd/yyyy", "M/d/yyyy", "yyyy-MM-dd", "MM/dd/yy"];
      for (const formatStr of formatsWithYear) {
        try {
          const parsed = parse(value, formatStr, new Date());
          if (!isNaN(parsed.getTime())) {
            // Check if date is within valid range
            if (isBefore(parsed, minDate ?? MIN_DATE) || isAfter(parsed, MAX_DATE)) {
              return fallback;
            }
            return parsed;
          }
        } catch (e) {
          // Try next format
        }
      }

      // Try formats without year and default to current year
      const formatsWithoutYear = ["MMM d", "MM/dd", "M/d"];
      for (const formatStr of formatsWithoutYear) {
        try {
          const parsed = parse(value, formatStr, new Date(currentYear, 0, 1));
          if (!isNaN(parsed.getTime())) {
            // Set the year to current year
            parsed.setFullYear(currentYear);
            // Check if date is within valid range
            if (isBefore(parsed, minDate ?? MIN_DATE) || isAfter(parsed, MAX_DATE)) {
              return fallback;
            }
            return parsed;
          }
        } catch (e) {
          // Try next format
        }
      }

      // Last resort: try native Date parsing
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        // Check if date is within valid range
        if (isBefore(date, minDate ?? MIN_DATE) || isAfter(date, MAX_DATE)) {
          return fallback;
        }
        return date;
      }
      return fallback;
    } catch (e) {
      return fallback;
    }
  };

  const handleFocusStart = () => {
    if (pendingRange?.from) {
      setCurrentMonth(pendingRange.from);
    }
  };

  const handleFocusEnd = () => {
    if (pendingRange?.to) {
      setCurrentMonth(pendingRange.to);
    }
  };

  const applyPendingRange = () => {
    // this should almost never be null is we are saving.
    onChange({
      start: pendingRange?.from,
      end: pendingRange?.to,
    });
    setOpen(false);
  };

  // Parse inputs without falling back to previous values; undefined means invalid or empty
  const parsedStartInput = useMemo(() => parseInputDate(startInputValue), [startInputValue]);
  const parsedEndInput = useMemo(() => parseInputDate(endInputValue), [endInputValue]);

  const hasStartInputError = useMemo(() => {
    if (!startInputValue.trim()) {
      return false;
    }
    if (!parsedStartInput) {
      return true;
    }
    if (parsedEndInput && parsedStartInput > parsedEndInput) {
      return true;
    }
    return false;
  }, [startInputValue, parsedStartInput, parsedEndInput]);

  const hasEndInputError = useMemo(() => {
    if (!endInputValue.trim()) {
      return false;
    }
    if (!parsedEndInput) {
      return true;
    }
    if (parsedStartInput && parsedEndInput < parsedStartInput) {
      return true;
    }
    return false;
  }, [endInputValue, parsedStartInput, parsedEndInput]);

  const trigger = renderTrigger ? (
    typeof renderTrigger === "function" ? (
      renderTrigger({ formatted: formattedDateRange })
    ) : (
      renderTrigger
    )
  ) : (
    <Button variant="outline" className="min-h-[40px] px-3 w-auto justify-start font-normal">
      <CalendarIcon className="w-4 h-4 mr-2" />
      {formattedDateRange ?? <span>Pick date</span>}
    </Button>
  );

  // Format the input only if the typed value is a valid date; otherwise keep user input
  const handleInputBlur = (type: "start" | "end") => {
    if (type === "start") {
      const d = parseInputDate(startInputValue);
      if (d) {
        setStartInputValue(format(d, "MMM d, yyyy"));
      }
    } else if (type === "end") {
      const d = parseInputDate(endInputValue);
      if (d) {
        setEndInputValue(format(d, "MMM d, yyyy"));
      }
    }
  };

  const isDoneDisabled = useMemo(() => {
    if (hasStartInputError || hasEndInputError) {
      return true;
    }
    if (pendingRange?.from && pendingRange?.to && pendingRange.from > pendingRange.to) {
      return true;
    }
    return false;
  }, [hasStartInputError, hasEndInputError, pendingRange]);

  // Reset any un-applied pending changes when the popover closes
  const resetToAppliedValues = () => {
    if (!start) {
      setPendingRange(undefined);
      setStartInputValue("");
      setEndInputValue("");
      return;
    }
    const appliedFrom = start;
    const appliedTo = end ?? start;
    setPendingRange({ from: appliedFrom, to: appliedTo });
    setStartInputValue(format(appliedFrom, "MMM d, yyyy"));
    setEndInputValue(format(appliedTo, "MMM d, yyyy"));
  };

  return (
    <div className="flex flex-col gap-1">
      {label && (
        <label className="text-xs font-semibold text-muted-foreground uppercase">{label}</label>
      )}
      <Popover
        open={open}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            // User dismissed without pressing Done or Clear; revert local edits
            resetToAppliedValues();
          }
          setOpen(nextOpen);
        }}
      >
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>

        <PopoverContent className="flex p-0 w-auto" align={contentAlign}>
          {/* presets */}
          <div className="flex flex-col border-r p-2 w-40 space-y-1">
            <div className="font-semibold text-sm mb-2">Presets</div>
            <Button
              key="custom"
              variant={selectedPreset === "Custom" ? "primary" : "ghost"}
              className="h-8 text-sm justify-start"
              onClick={() => {
                // Clear the dates so user can select custom range
                setPendingRange(undefined);
                setStartInputValue("");
                setEndInputValue("");
                setCurrentMonth(new Date()); // Reset to current month view
              }}
            >
              Custom
            </Button>
            {presets.map(({ label, range }) => (
              <Button
                key={label}
                variant={selectedPreset === label ? "primary" : "ghost"}
                className="h-8 text-sm justify-start"
                onClick={() => {
                  const r = range();
                  // Update pending range and input values only
                  setPendingRange({ from: r.from, to: r.to });
                  if (r.from) {
                    setStartInputValue(format(r.from, "MMM d, yyyy"));
                    setCurrentMonth(r.from);
                  }
                  if (r.to) {
                    setEndInputValue(format(r.to, "MMM d, yyyy"));
                  }
                }}
              >
                {label}
              </Button>
            ))}
          </div>

          {/* calendar and date inputs */}
          <div className="p-4 flex flex-col">
            <div className="grid grid-cols-2 gap-4 mb-4 px-3">
              <div className="space-y-1">
                <Label htmlFor="start-date-input" className="text-xs">
                  Start
                </Label>
                <DateInputField
                  id="start-date-input"
                  value={startInputValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setStartInputValue(value);
                    const date = parseInputDate(value);
                    if (!date) {
                      return;
                    }
                    // If the end input has a valid parsed date, ensure start <= end
                    if (parsedEndInput && date > parsedEndInput) {
                      return;
                    }
                    setPendingRange(prev => ({ from: date, to: prev?.to }));
                  }}
                  onBlur={() => handleInputBlur("start")}
                  onFocus={handleFocusStart}
                  autoFocus={true}
                  hasError={hasStartInputError}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="end-date-input" className="text-xs">
                  End
                </Label>
                <DateInputField
                  id="end-date-input"
                  value={endInputValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const value = e.target.value;
                    setEndInputValue(value);
                    const date = parseInputDate(value);
                    if (!date) {
                      return;
                    }
                    // If the start input has a valid parsed date, ensure end >= start
                    if (parsedStartInput && date < parsedStartInput) {
                      return;
                    }
                    setPendingRange(prev => ({ from: prev?.from, to: date }));
                  }}
                  onBlur={() => handleInputBlur("end")}
                  onFocus={handleFocusEnd}
                  hasError={hasEndInputError}
                />
              </div>
            </div>

            <div>
              <Calendar
                mode="range"
                numberOfMonths={2}
                classNames={{
                  range_end:
                    "day-range-end [&>button]:!bg-[#137A4A] [&>button]:text-white [&>button]:hover:!bg-[#137A4A] [&>button]:hover:text-white [&>button]:focus:!bg-[#137A4A] [&>button]:focus:text-white",
                  range_start:
                    "day-range-start [&>button]:!bg-[#137A4A] [&>button]:text-white [&>button]:hover:!bg-[#137A4A] [&>button]:hover:text-white [&>button]:focus:!bg-[#137A4A] [&>button]:focus:text-white",
                  range_middle:
                    "!bg-[#e9f5ee] hover:!bg-[#d3eadc] rounded-none [&>button]:!bg-transparent [&>button]:!text-gray-800",
                  outside:
                    "day-outside [&>button]:text-muted-foreground [&.day-range-start>button]:!text-white [&.day-range-end>button]:!text-white [&.day-range-start>button]:!bg-[#137A4A] [&.day-range-end>button]:!bg-[#137A4A]",
                }}
                selected={pendingRange}
                onSelect={range => {
                  setPendingRange(range);
                  if (range?.from) {
                    const fromDate: Date = range.from;
                    setStartInputValue(format(fromDate, "MMM d, yyyy"));
                  }
                  if (range?.to) {
                    const toDate: Date = range.to;
                    setEndInputValue(format(toDate, "MMM d, yyyy"));
                  }
                }}
                autoFocus
                month={currentMonth}
                onMonthChange={setCurrentMonth}
                startMonth={minDate ?? MIN_DATE}
                endMonth={MAX_DATE}
                disabled={minDate ? { before: minDate } : undefined}
              />
            </div>

            {/* Element for end date scrolling - positioned after first month in the calendar */}
            <div className="h-2 mt-2" />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setPendingRange(undefined);
                  setStartInputValue("");
                  setEndInputValue("");
                  onChange({ start: undefined, end: undefined });
                  setOpen(false);
                }}
              >
                Clear
              </Button>
              <Button size="sm" onClick={applyPendingRange} disabled={isDoneDisabled}>
                Done
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
